import { addTo } from '../../../util/index.js';
import { FixedDistributionDateBehavior } from '../../fixed-distributions/index.js';
import { isCalculatedInterest } from '../../vouchers/setup.js';
import { distributeBetween } from './allocation.js';
import { settleDerived } from './derived.js';
import { consume, ledgerOpen } from './ledger.js';
// A correction applies as if that part of the target never existed, up to
// what its debt side holds: the rest of the credit stays free for other debt,
// as in v1. Interest has no such bound up front, as it has yet to accrue: a
// correction of interest takes the oldest accrual as it accrues, and is
// lowered to what the interest held by its credit's date (E7, see
// noteCorrectionCaps). Returns the amount applied.
export const applyCorrection = (source, target, amount, fixedDistributionId) => {
    const applied = Math.min(amount, ...[source, target]
        .filter((side) => side.kind === 'debt')
        .map((side) => ledgerOpen(side.ledger)));
    if (applied <= 0) {
        return 0;
    }
    for (const side of [source, target]) {
        if (side.ledger === null) {
            continue;
        }
        if (side.kind === 'interest') {
            side.ledger.waiver += applied;
        }
        else {
            consume(side.ledger, applied);
        }
    }
    distributeBetween(source, target, applied, target.date, target.date, fixedDistributionId);
    return applied;
};
// A correction of interest takes no more than the interest held by its
// credit's date, as a correction of debt takes no more than the debt (E7); the
// rest of the credit pays other debt. In credit date order, each takes what
// the earlier ones left. A correction of joint interest applies alike on
// every case of the association, uncapped: capping it on each would make each
// case's cap depend on the others'. What a joint credit note corrects is
// capped on its own case, the others mirroring what it applied there; while
// the replay searches what that is, `releaseCreditCaps` frees what a credit
// note did not find for it to pay at its date in the same pass (D11).
export const noteCorrectionCaps = (caseState, source, releaseCreditCaps) => {
    for (const { id, source: from, target, applied, byCreditNote, } of caseState.corrections) {
        if (from !== source ||
            target.kind !== 'interest' ||
            (target.associationId != null && !byCreditNote)) {
            continue;
        }
        const ledger = target.ledger;
        settleDerived(target);
        const found = ledger.corrected +
            Math.min(ledger.waiver, ledgerOpen(ledger)) -
            (caseState.correctionsFound.get(ledger) ?? 0);
        const cap = Math.min(applied, found);
        addTo(caseState.correctionsFound, ledger, cap);
        if (cap < applied) {
            caseState.correctionCaps.set(id, cap);
            if (byCreditNote && releaseCreditCaps) {
                const freed = applied - cap;
                ledger.waiver = Math.max(0, ledger.waiver - freed);
                distributeBetween(from, target, -freed, target.date, target.date, undefined);
            }
        }
    }
};
// Whether a credit of the case corrects its calculated interest.
export const correctsInterest = (input) => {
    const byId = new Map(input.caseVouchers.map((voucher) => [voucher.caseVoucherId, voucher]));
    return input.fixedDistributions.some((fd) => {
        const source = byId.get(fd.fromCaseVoucherId);
        const target = byId.get(fd.toCaseVoucherId);
        return (fd.dateBehavior === FixedDistributionDateBehavior.FromOriginal &&
            source !== undefined &&
            target !== undefined &&
            target.associationId == null &&
            isCalculatedInterest(target));
    });
};
//# sourceMappingURL=corrections.js.map